import { urlComSessao } from "./sessao";
import type { Row, DashboardData, ItemAlmox, Separacao, EnvioLab } from "./types";
import type { ColunaKanban } from "./kanban";

const BASE = "/api";

/**
 * Usuário logado nesta sessão. É enviado no header das operações de usuários
 * para o servidor validar que apenas administradores criam/editam/excluem.
 */
let sessionUser = "";
export function setSessionUser(u: string) {
  sessionUser = u ?? "";
}
const authHeaders = () => ({ "x-sercamp-usuario": sessionUser });

/** Uma observação da célula da agenda (ver server/agenda-observacoes.ts). */
export interface Observacao {
  id: number;
  texto: string;
  autor: string | null;
  created_at: string;
}

export class ApiError extends Error {
  code: string;
  constructor(message: string, code = "ERROR") {
    super(message);
    this.code = code;
  }
}

async function handle(res: Response) {
  if (!res.ok) {
    let msg = `Erro ${res.status}`;
    let code = "ERROR";
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
      if (body?.code) code = body.code;
    } catch {
      /* ignora */
    }
    throw new ApiError(msg, code);
  }
  return res.json();
}

/**
 * Igual ao `handle`, mas o 409 de CONFLITO volta como resposta em vez de erro.
 *
 * O agendamento manual usa o 409 para dizer "essa data choca com a agenda de
 * fulano" — e a tela precisa da LISTA de choques para mostrar quem é e deixar o
 * usuário decidir. Tratar isso como exceção jogaria fora justamente o conteúdo
 * que resolve a dúvida dele. Erro de verdade (400/500) continua estourando.
 */
async function handleComConflito(res: Response) {
  if (res.status === 409) {
    const body = await res.json().catch(() => null);
    if (body?.conflito) return body;
  }
  return handle(res);
}

export const api = {
  list: (resource: string): Promise<Row[]> =>
    fetch(`${BASE}/${resource}`, { headers: authHeaders() }).then(handle),

  /**
   * MARCA UM PERÍODO na agenda de um colaborador ("de tal data até tal data").
   *
   * Sem `modo`, dias com serviço de outra pessoa fazem o servidor devolver
   * `{ conflito: true, substituiveis, bloqueados }` (HTTP 409, nada gravado)
   * para a tela perguntar antes de passar por cima. Folga e OS aprovada nunca
   * são sobrescritas — vêm em `bloqueados` e continuam como estão.
   */
  agendaPeriodo: (payload: {
    /**
     * Um dos dois — colaborador do quadro OU freelancer da agenda. Pode faltar
     * quando há `participantes`: no pop-up de um dia só, a célula clicada é
     * gravada pelo CRUD e só os participantes passam pelo lote.
     */
    colaborador_id?: number;
    freelancer_id?: number;
    inicio: string;
    fim: string;
    servico: string;
    cor: string;
    incluir_fim_de_semana?: boolean;
    modo?: "pular" | "substituir";
    /** Recados do planejador — cada um gravado em CADA dia efetivamente marcado. */
    observacoes?: string[];
    /** Quem recebe a MESMA marcação (o card "Participantes" do pop-up). */
    participantes?: ({ colaborador_id: number } | { freelancer_id: number })[];
  }): Promise<{
    ok?: boolean;
    conflito?: boolean;
    colaborador?: string | null;
    colaborador_id?: number;
    freelancer_id?: number;
    inicio: string;
    fim: string;
    dias?: string[];
    criados?: string[];
    atualizados?: string[];
    pulados?: { data: string; tipo: string; motivo: string }[];
    /** Cada item diz DE QUEM é o dia (`pessoa`) — o lote pode ter várias. */
    substituiveis?: { data: string; servico: string | null; cor: string; pessoa?: string; principal?: boolean }[];
    bloqueados?: { data: string; tipo: string; motivo: string; pessoa?: string; principal?: boolean }[];
    /** Todas as pessoas do lote: a dona da célula primeiro, depois os participantes. */
    pessoas?: {
      tipo: "colaborador" | "freelancer";
      id: number;
      nome: string;
      principal: boolean;
      dias: string[];
      criados: string[];
      atualizados: string[];
      pulados: { data: string; tipo: string; motivo: string }[];
    }[];
    total_previsto?: number;
    fins_de_semana_pulados?: number;
    observacoes_criadas?: number;
    /** Quantas deveriam existir — o front compara e avisa quando falta. */
    observacoes_esperadas?: number;
    observacoes_apagadas?: number;
    mensagem?: string;
  }> =>
    fetch(`${BASE}/agenda/periodo`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(payload),
    }).then(handleComConflito),

  /* ------------------- OBSERVAÇÕES DA CÉLULA DA AGENDA -------------------
   * Os recados do planejador para aquele dia. São VÁRIOS por dia (cada um com
   * autor e data), por isso vivem numa rota própria e não num campo da célula.
   * Ver server/agenda-observacoes.ts. */
  agendaObservacoes: (agendaId: number): Promise<Observacao[]> =>
    fetch(`${BASE}/agenda/${agendaId}/observacoes`, { headers: authHeaders() }).then(handle),

  agendaObservacaoCriar: (agendaId: number, texto: string): Promise<Observacao> =>
    fetch(`${BASE}/agenda/${agendaId}/observacoes`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ texto }),
    }).then(handle),

  agendaObservacaoRemover: (agendaId: number, obsId: number): Promise<{ ok: boolean }> =>
    fetch(`${BASE}/agenda/${agendaId}/observacoes/${obsId}`, {
      method: "DELETE",
      headers: authHeaders(),
    }).then(handle),

  create: (resource: string, data: Row): Promise<Row> =>
    fetch(`${BASE}/${resource}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),

  update: (resource: string, id: number, data: Row): Promise<Row> =>
    fetch(`${BASE}/${resource}/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),

  remove: (resource: string, id: number): Promise<{ ok: boolean }> =>
    fetch(`${BASE}/${resource}/${id}`, {
      method: "DELETE",
      headers: authHeaders(),
    }).then(handle),

  dashboard: (): Promise<DashboardData> =>
    fetch(`${BASE}/dashboard`, { headers: authHeaders() }).then(handle),

  /**
   * Puxa o cadastro de colaboradores do RH/DP (fonte única do portal) para a
   * base deste sistema. Usado pelo botão "Sincronizar" da aba Equipe.
   */
  sincronizarColaboradores: (): Promise<ResultadoSyncColaboradores> =>
    fetch(`${BASE}/colaboradores/sincronizar`, {
      method: "POST",
      headers: authHeaders(),
    }).then(handle),

  /** Retorna { usuario, cargo, isAdmin, podeExcluir } do usuário informado. */
  me: (
    usuario: string,
  ): Promise<{
    usuario: string;
    nome: string | null;
    cargo: string | null;
    isAdmin: boolean;
    /** Só administradores excluem registros (regra do Portal). */
    podeExcluir: boolean;
  }> => fetch(`${BASE}/me?u=${encodeURIComponent(usuario)}`).then(handle),

  /** Importa as Ordens de Serviço do OMIE para as Atividades. */
  importarOmie: (
    paginas = 1,
  ): Promise<{
    inseridas: number;
    atualizadas: number;
    arquivadas: number;
    ignoradas: number;
    total: number;
    paginas: number;
    total_os_omie: number;
  }> =>
    fetch(`${BASE}/atividades/importar-omie?paginas=${paginas}`, {
      method: "POST",
      headers: authHeaders(),
    }).then(handle),

  /**
   * KANBAN — move o cartão da atividade para outra coluna. Grava só NESTE
   * sistema (o OMIE não é alterado). Volta os campos do quadro + a etapa do
   * OMIE, para a tela juntar na linha que já tem. Ver server/kanban.ts.
   */
  moverKanban: (
    id: number,
    coluna: ColunaKanban,
  ): Promise<{
    id: number;
    etapa_omie: string | null;
    kanban_coluna: ColunaKanban;
    kanban_etapa_ref: string | null;
    kanban_movido_em: string;
    kanban_movido_por: string | null;
  }> =>
    fetch(`${BASE}/atividades/${id}/kanban`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ coluna }),
    }).then(handle),

  /** Estado da integração OMIE (indicador visível na aba Atividades). */
  omieStatus: (): Promise<{
    configurado: boolean;
    sincronizando: boolean;
    ultima_sincronizacao: string | null;
    total_os_omie: number | null;
    ativas: number;
  }> => fetch(`${BASE}/omie/status`, { headers: authHeaders() }).then(handle),

  /** Checkpoint de execução de uma atividade + geração da opção de data. */
  checkpoint: {
    list: (): Promise<
      Array<{
        atividade_id: number;
        data_sugerida: string | null;
        data_confirmada: string | null;
        opcao_escolhida: string | null;
        preenchido_em: string;
      }>
    > => fetch(`${BASE}/checkpoints`, { headers: authHeaders() }).then(handle),

    get: (atividadeId: number): Promise<Row | null> =>
      fetch(`${BASE}/atividades/${atividadeId}/checkpoint`, { headers: authHeaders() }).then(handle),

    save: (atividadeId: number, data: Row): Promise<Row> =>
      fetch(`${BASE}/atividades/${atividadeId}/checkpoint`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    sugerirData: (
      atividadeId: number,
    ): Promise<{ data_sugerida: string; dias: number; regime: string }> =>
      fetch(`${BASE}/atividades/${atividadeId}/sugerir-data`, {
        method: "POST",
        headers: authHeaders(),
      }).then(handle),
  },

  /** Planejamento (IA): gera/confirma datas, marca a agenda e integra documentos do DP. */
  planejamento: {
    opcoesData: (
      atividadeId: number,
    ): Promise<{ opcoes: any[]; dias: number; qtd: number; regime: string; leitura: LeituraIA }> =>
      fetch(`${BASE}/atividades/${atividadeId}/opcoes-data`, { method: "POST", headers: authHeaders() }).then(handle),

    /** Envia as 3 datas para a APROVAÇÃO do Gestor Operacional (não marca a agenda). */
    preAgendar: (
      atividadeId: number,
    ): Promise<{ ok: boolean; aprovacao_id: number; mensagem: string }> =>
      fetch(`${BASE}/atividades/${atividadeId}/pre-agendar`, { method: "POST", headers: authHeaders() }).then(handle),

    /** Confirma a data com o cliente e envia para a APROVAÇÃO do Gestor Operacional. */
    confirmarData: (
      atividadeId: number,
      indice: number,
    ): Promise<{ ok: boolean; escolhida: { inicio: string }; aprovacao_id: number; mensagem: string }> =>
      fetch(`${BASE}/atividades/${atividadeId}/confirmar-data`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ indice }),
      }).then(handle),

    /**
     * Opção 03 — a data que o PLANEJAMENTO escolhe.
     * `verificarData` só consulta: devolve a janela que aquela data gera e quem
     * da equipe está livre/ocupado nela (a IA continua lendo agenda + pessoas,
     * só não decide a data). `agendarData` grava e manda para o gestor.
     */
    verificarData: (atividadeId: number, inicio: string): Promise<VerificacaoData> =>
      fetch(`${BASE}/atividades/${atividadeId}/verificar-data`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ inicio }),
      }).then(handle),

    agendarData: (
      atividadeId: number,
      inicio: string,
      equipe: number[],
      forcar = false,
    ): Promise<AgendamentoManual> =>
      fetch(`${BASE}/atividades/${atividadeId}/agendar-data`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ inicio, equipe, forcar }),
      }).then(handleComConflito),

    documentos: (atividadeId: number, colaboradorId?: number): Promise<any> =>
      fetch(
        `${BASE}/atividades/${atividadeId}/documentos${colaboradorId ? `?colaboradorId=${colaboradorId}` : ""}`,
        { headers: authHeaders() },
      ).then(handle),

    zipUrl: (atividadeId: number, colaboradorId?: number): string =>
      urlComSessao(`${BASE}/atividades/${atividadeId}/documentos.zip${colaboradorId ? `?colaboradorId=${colaboradorId}` : ""}`),
  },

  /**
   * SEPARAÇÃO DE MATERIAL — a OS pedindo o que precisa.
   *
   * Nenhuma destas chamadas fala direto com o Almoxarifado ou com o Laboratório:
   * quem faz isso é o servidor daqui (ver server/almoxarifado.ts e
   * server/laboratorio.ts). O navegador não pode carregar o segredo das
   * integrações — ele estaria à vista de qualquer um com F12 aberto.
   */
  separacao: {
    /** Estoque do Almoxarifado para escolher o que separar. */
    itens: (): Promise<ItemAlmox[]> =>
      fetch(`${BASE}/almoxarifado/itens`, { headers: authHeaders() }).then(handle),

    /** Pedidos de separação; com `os`, só os daquela Ordem de Serviço. */
    separacoes: (os?: string): Promise<Separacao[]> =>
      fetch(`${BASE}/almoxarifado/separacoes${os ? `?os=${encodeURIComponent(os)}` : ""}`, {
        headers: authHeaders(),
      }).then(handle),

    /** Abre no Almoxarifado uma separação por insumo da lista. */
    pedir: (
      atividadeId: number,
      dados: {
        itens: { item_id: number; quantidade: number }[];
        prioridade?: string;
        os_numero?: string;
        cliente?: string;
        atividade?: string;
        observacao?: string;
      },
    ): Promise<{ ok: boolean; criadas: number; falhas: { item_id: number; erro: string }[] }> =>
      fetch(`${BASE}/atividades/${atividadeId}/separacao`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    /** Cria a remessa (e a análise) da OS no Laboratório. */
    enviarLaboratorio: (
      atividadeId: number,
      dados: {
        quantidade_prevista: number;
        equipamento?: string;
        observacao?: string;
        criar_analise?: boolean;
        prazo_coleta?: string;
        prazo_retorno?: string;
        analises?: string[];
      },
    ): Promise<{
      ok: boolean;
      osNumber: string;
      materialId: number;
      analiseId: number | null;
      remessaReaproveitada: boolean;
      analiseReaproveitada: boolean;
    }> =>
      fetch(`${BASE}/atividades/${atividadeId}/enviar-laboratorio`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    /** O que já foi enviado, com o andamento lido do Laboratório. */
    envios: (): Promise<{ envios: EnvioLab[]; labOffline: boolean; motivo?: string }> =>
      fetch(`${BASE}/laboratorio/envios`, { headers: authHeaders() }).then(handle),
  },
};

/** Um colaborador diante da data escolhida: livre ou já comprometido. */
export interface PessoaNaData {
  id: number;
  nome: string;
  /** Quanto as skills/setor batem com o serviço (ordena a sugestão). */
  afinidade: number;
  livre: boolean;
  conflitos: { data: string; motivo: string }[];
}

/** O que a IA leu para responder — mostrado na tela para dar confiança. */
export interface LeituraIA {
  colaboradores_ativos: number;
  marcacoes_agenda: number;
  pedidos_pendentes: number;
}

/** Resposta de `verificarData`: a janela daquela data e quem está livre nela. */
export interface VerificacaoData {
  inicio: string;
  fim: string;
  dias_uteis: string[];
  rotulo: string;
  dias: number;
  qtd: number;
  regime: string;
  servico: string;
  hoje: string;
  pessoas: PessoaNaData[];
  sugestao: { id: number; nome: string }[];
  livres: number;
  conflito: boolean;
  avisos: string[];
  leitura: LeituraIA;
}

/**
 * Resposta de `agendarData`. Ou o pedido foi aberto (`ok`), ou voltou um
 * CONFLITO com o que impede — e aí a tela pergunta se envia assim mesmo.
 */
export type AgendamentoManual =
  | {
      ok: true;
      /** Presente e falso no sucesso: é o discriminante que a tela testa. */
      conflito?: false;
      escolhida: { inicio: string; rotulo: string; equipe: { id: number; nome: string }[] };
      aprovacao_id: number;
      avisos?: string[];
      mensagem: string;
    }
  | (VerificacaoData & {
      conflito: true;
      equipe_escolhida: { id: number; nome: string }[];
      ocupados: { id: number; nome: string; conflitos: { data: string; motivo: string }[] }[];
      mensagem: string;
    });

/** Resultado do espelhamento do cadastro de colaboradores (RH/DP → aqui). */
export interface ResultadoSyncColaboradores {
  ok: boolean;
  criados: number;
  atualizados: number;
  ignorados: number;
  somenteLocais: number;
  total: number;
  fonte: string | null;
  mensagem: string;
  /**
   * Quem o RH/DP tem mas NÃO é equipe de campo, com o motivo. Vem separado
   * da `mensagem` de propósito: a tela mostra só a contagem e abre a lista
   * quando o usuário pede (ver CrudView).
   */
  naoSaoDeCampo?: { nome: string; motivo: string }[];
}
